import React from 'react';
import type { ViewProps } from 'react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import {
  BodyText,
  Icon,
  theme,
  useBreakpointHelpers,
  VStack,
} from '@sweetgreen/garnish';

import type { GetBillingHistoryQuery } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { AccountScreenTitle } from '../../components/Account';
import { BRAINTREE_CREDIT_CARDS_ICONS } from '../SweetpassCheckoutScreen/SweetpassCheckoutCardBlock';

export const SweetpassBillingHistoryScreenContent = ({
  testID,
  billingHistory,
}: SweetpassBillingHistoryScreenContentProps) => {
  const { t, formatDate, formatPrice } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();

  return (
    <ScrollView contentContainerStyle={styles.scrollContainer}>
      <View testID={testID?.concat('.content')} style={styles.body}>
        <AccountScreenTitle
          title={t('account.sweetpass-membership.billing-history.header')}
        />
        <VStack style={styles.billingHistoryList} divider>
          {billingHistory.map(({ id, creditCard, amount, date, refund }) => (
            <View
              key={id}
              testID={testID?.concat(`.item-${id}`)}
              style={styles.billingHistoryItem}
            >
              <View style={styles.billingHistoryItemDetails}>
                {isMobile ? null : (
                  <Icon
                    style={{ marginRight: theme.spacing.large }}
                    name={BRAINTREE_CREDIT_CARDS_ICONS[creditCard.cardType]}
                  />
                )}
                <View>
                  <BodyText size={4}>{formatDate(date)}</BodyText>
                  <BodyText
                    size={5}
                    style={styles.billingHistoryItemDescription}
                  >
                    {t('sweetpass.checkout.credit-card.description', {
                      cardType: creditCard.cardType,
                      lastFour: creditCard.lastFour,
                    })}
                  </BodyText>
                </View>
              </View>
              <View>
                <BodyText size={4}>
                  {formatPrice(Number(amount) * (refund ? -100 : 100), 'USD')}
                </BodyText>
              </View>
            </View>
          ))}
        </VStack>
      </View>
    </ScrollView>
  );
};

type SweetpassBillingHistoryScreenContentProps = {
  readonly billingHistory: GetBillingHistoryQuery['getSubscriptions'][0]['billingHistory'];
} & Pick<ViewProps, 'testID'>;

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    paddingTop: theme.spacing.large,
  },
  body: {
    paddingBottom: theme.spacing.large,
    paddingHorizontal: theme.spacing.large,
  },
  billingHistoryList: {
    paddingVertical: theme.spacing.large,
    width: '100%',
    borderBottomColor: theme.colors.GREEN_1,
    borderBottomWidth: 1,
  },
  billingHistoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  billingHistoryItemDetails: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  billingHistoryItemDescription: {
    paddingTop: theme.spacing.xsmall,
    color: theme.colors.NEUTRAL_2,
  },
});
